import { BrowserModule } from '@angular/platform-browser';
import { Router } from "@angular/router";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ScrollingModule } from '@angular/cdk/scrolling';
import * as Sentry from "@sentry/angular";

import { AppRouting } from './app.routing';
import { AppComponent } from './app.component';
import { TopNavComponent } from './top-nav/top-nav.component';
import { SideNavComponent } from './side-nav/side-nav.component';
import { VersionBannerComponent } from './components/version-banner/version-banner.component';

import { ApiService } from './services/api.service';
import { UsersService } from './services/users.service';
import { CommonService } from './services/common.service';
import { DealerService } from './services/dealer.service';
import { RolesService } from './services/roles.service';
import { AccessesService } from './services/accesses.service';
import { SitesService } from './services/sites.service';
import { DevicesService } from './services/devices.service';
import { GlobalErrorHandler } from './services/errorHandler.service';
import { HttpErrorInterceptor } from './services/httpIntercepter.service';
import { PagerService } from './services/pager.service';
import { TosService } from './services/tos.service';
import { ToastService } from './services/toast.service';
import { LogsService } from './services/logs.service';
import { InvoicesService } from './services/invoices.service';
import { WebsocketService } from './services/websocket.service';
import { IntegrationService } from './services/integration.service';
import { PermissionsService } from './services/permissions.service';
import { PermissionCheckService } from './services/permission-check.service';
import { WebworkerService } from './services/webworker.service';
import { PaymentService } from './services/payment.service';
import { MastermindsService } from './services/masterminds.service';
import { PartnerService } from './services/partner.service';
import { EventsService } from './services/events.service';
import { AudioService } from './services/audio.service';
import { NotificationsService } from './services/notifications.service';
import { ThirdPartyService } from './services/thirdparty.service';
import { ScriptService } from './services/script.service';
import { StatisticsService } from './services/statistics.service';
import { DealerPlanService } from './services/service-plan.service';
import { VerificationService } from './services/verification.service';
import { VideoAIService } from './services/cloud-ai.service';
import { IntentAnalysisService } from './services/intent-analysis.service';
import { DivisionService } from './services/divisions.service'
import { VirtualGuardTourService } from './services/virtual-guard-tour.service';
import { EventStoragesService } from './services/event-storages.service';
import { EventViewerService } from './services/event-viewer.service';
import { IncidentReportsService } from './services/incident-reports.service';
import { NoteDirectionsService } from './services/note-directions.service';
import { MpSettingsService } from './services/mp_settings.service';
import { MpSessionsService } from './services/mp-sessions.service';

import { c_components_module } from '../3ui/2components/index.module';
import { HelperModule } from '../4services/2helper/index.module';
import { subscribers_module } from '../7subscribers/subscribers_module';
import { ProfileModule } from './modals/profile/profile.module';
import { DealerPlanModalModule } from './modals/dealer-plan/dealer-plan-modal.module';
import { DealerPlanForStandardModule } from './modals/dealer-plan-for-standard/dealer-plan-for-standard-modal.module';
import { ToastModule } from './modals/toast/toast.module';
import { NoticeModule } from './modals/notice/notice.module';
import { MarkdownModule } from 'ngx-markdown';
// DeviceDetectorModule removed in ngx-device-detector v7 - now uses standalone service
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { MqttModule, IMqttServiceOptions } from "ngx-mqtt";
import { SharedModule } from './directives/shared.module';
import { ToastrModule } from 'ngx-toastr';

// Chart.js v3 global registration
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { LineWithLineController } from './components/chart/line-with-line.controller';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

import config from "../1config"
import { ensureFreshToken } from "../2loaders/auth0_auth"
let client_id = "operation-client-" + Math.random().toString(16).substr(2, 8);

import { SortService } from './services/sort.service';

/**
 * Initialize user data before app starts
 * Handles dealer_id from query params for temporary login
 */
export function initializeUserData(
  usersService: UsersService,
  permissionsService: PermissionsService,
  commonService: CommonService,
): () => Promise<void> {
  return async () => {
    try {
      // 만료된 쿠키 토큰으로 부팅하면 초기 API가 401을 받으므로 갱신 완료까지 대기 (CHEKT-DEALER-3KJ)
      await ensureFreshToken();

      // Extract dealer_id from URL query params
      const url = window.location.href;
      const params = commonService.getQueryParams(url.split('?')[1]);
      const dealer_id = params['dealer_id'] ? Number(params['dealer_id']) : undefined;

      // Load user data
      await usersService.loadMe(dealer_id);

      // Load permissions after user data is ready
      const currentDealerId = usersService.dealerId();
      if (currentDealerId) {
        await permissionsService.loadPermissions(currentDealerId);
      }

      console.log('[APP_INITIALIZER] User data loaded successfully');
    } catch (error) {
      console.error('[APP_INITIALIZER] Failed to load user data:', error);

      // Retry once on failure
      try {
        console.log('[APP_INITIALIZER] Retrying...');
        await usersService.loadMe();
        const currentDealerId = usersService.dealerId();
        if (currentDealerId) {
          await permissionsService.loadPermissions(currentDealerId);
        }
      } catch (retryError) {
        console.error('[APP_INITIALIZER] Retry failed. App may not work properly.');
        // Allow app to start anyway (error will be handled by components)
      }
    }
  };
}

export const MQTT_SERVICE_OPTIONS: IMqttServiceOptions = {
  protocol:'wss',
  hostname: config.command_center_url,
  port: 443,
  path: '/mqtt',
  clientId: client_id,
  username: 'app',
  keepalive: 50
}

// Register Chart.js plugins and custom controllers globally for v3
Chart.register(...registerables, annotationPlugin, ChartDataLabels, LineWithLineController);

@NgModule({ declarations: [
        AppComponent,
        TopNavComponent,
        SideNavComponent,
        VersionBannerComponent,
    ],
    bootstrap: [AppComponent], imports: [c_components_module,
        HelperModule,
        subscribers_module,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        AppRouting,
        ScrollingModule,
        ProfileModule,
        DealerPlanModalModule,
        DealerPlanForStandardModule,
        ToastModule,
        NoticeModule,
        MarkdownModule,
        MarkdownModule.forRoot(),
        NgxDaterangepickerMd.forRoot(),
        SharedModule,
        MqttModule.forRoot(MQTT_SERVICE_OPTIONS),
        ToastrModule.forRoot({
            timeOut: 5000,
            positionClass: 'toast-bottom-center',
            closeButton: true,
            progressBar: true,
        })], providers: [
        // APP_INITIALIZER: Load user data before app starts
        {
            provide: APP_INITIALIZER,
            useFactory: initializeUserData,
            deps: [UsersService, PermissionsService, CommonService],
            multi: true
        },
        ApiService,
        UsersService,
        AccessesService,
        TosService,
        LogsService,
        InvoicesService,
        WebsocketService,
        CommonService,
        DealerService,
        PagerService,
        SitesService,
        DevicesService,
        RolesService,
        ToastService,
        IntegrationService,
        PermissionsService,
        PermissionCheckService,
        WebworkerService,
        MastermindsService,
        PaymentService,
        PartnerService,
        EventsService,
        AudioService,
        NotificationsService,
        SortService,
        ThirdPartyService,
        StatisticsService,
        ScriptService,
        DealerPlanService,
        VerificationService,
        VideoAIService,
        IntentAnalysisService,
        DivisionService,
        VirtualGuardTourService,
        EventStoragesService,
        EventViewerService,
        IncidentReportsService,
        NoteDirectionsService,
        MpSettingsService,
        MpSessionsService,
        DeviceDetectorService,
        // ErrorHandler: GlobalErrorHandler (Sentry 통합 완료)
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        // HTTP Interceptor
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        // Sentry TraceService (Performance Monitoring)
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideCharts(withDefaultRegisterables()),
    ] })
export class AppModule { 
  constructor(trace: Sentry.TraceService) {}
}
